import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Alert, AlertDescription } from './ui/alert';
import { Search, MapPin, Award, DollarSign, Eye, User, UserPlus, Users, Zap, Star } from 'lucide-react';
import PublicProfile from './PublicProfile';
import MutualConnectionsModal from './MutualConnectionsModal';
import ReviewsModal from './ReviewsModal';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ServiceProviders = () => {
  const [providers, setProviders] = useState([]);
  const [filteredProviders, setFilteredProviders] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [locationFilter, setLocationFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [selectedProvider, setSelectedProvider] = useState(null);
  const [networkAlert, setNetworkAlert] = useState(null);
  const [mutualConnectionsModal, setMutualConnectionsModal] = useState({ isOpen: false, connections: [], providerName: '' });
  const [reviewsModal, setReviewsModal] = useState({ isOpen: false, provider: null });

  useEffect(() => {
    fetchProviders();
  }, []);

  useEffect(() => {
    filterProviders();
  }, [providers, searchQuery, locationFilter]);

  const fetchProviders = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/providers/search`);
      if (response.data.status === 'success') {
        setProviders(response.data.providers);
      }
    } catch (error) {
      console.error('Error fetching providers:', error);
    } finally {
      setLoading(false);
    }
  };

  const filterProviders = () => {
    let filtered = providers;

    if (searchQuery.trim()) {
      filtered = filtered.filter(provider =>
        provider.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        provider.business_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        provider.services_provided.some(service =>
          service.toLowerCase().includes(searchQuery.toLowerCase())
        )
      );
    }

    if (locationFilter.trim()) {
      filtered = filtered.filter(provider =>
        provider.location?.toLowerCase().includes(locationFilter.toLowerCase())
      );
    }

    setFilteredProviders(filtered);
  };

  const getServiceColor = (service) => {
    const colors = [
      'bg-blue-100 text-blue-800',
      'bg-green-100 text-green-800',
      'bg-purple-100 text-purple-800',
      'bg-orange-100 text-orange-800',
      'bg-pink-100 text-pink-800',
      'bg-indigo-100 text-indigo-800'
    ];
    const index = service.length % colors.length;
    return colors[index];
  };

  const handleMutualConnectionsClick = (connections, providerName) => {
    setMutualConnectionsModal({
      isOpen: true,
      connections: connections,
      providerName: providerName
    });
  };

  const handleReviewsClick = (provider) => {
    setReviewsModal({
      isOpen: true,
      provider: provider
    });
  };

  const closeMutualConnectionsModal = () => {
    setMutualConnectionsModal({ isOpen: false, connections: [], providerName: '' });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2" style={{borderColor: 'var(--brand-navy)'}}></div>
        <span className="ml-2">Loading service providers...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Network Alert */}
      {networkAlert && (
        <Alert className="border-green-200 bg-green-50">
          <AlertDescription className="text-green-700">
            {networkAlert}
          </AlertDescription>
        </Alert>
      )}

      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold" style={{color: 'var(--brand-navy)'}}>
          Service Providers
        </h1>
        <p className="text-gray-600 mt-2">
          Discover trusted professionals in your network
        </p>
      </div>

      {/* Search and Filters */}
      <div className="bg-white/70 backdrop-blur-xl rounded-2xl p-6 shadow-lg border border-slate-200/50">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-5 h-5" />
            <Input
              placeholder="Search by name, business, or service..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>
          <div className="md:w-64 relative">
            <MapPin className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-5 h-5" />
            <Input
              placeholder="Filter by location..."
              value={locationFilter}
              onChange={(e) => setLocationFilter(e.target.value)}
              className="pl-10"
            />
          </div>
        </div>
      </div>

      {/* Results */}
      {filteredProviders.length === 0 ? (
        <div className="text-center py-16">
          <User className="w-16 h-16 text-slate-400 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-slate-600 mb-2">
            {providers.length === 0 ? "No service providers yet" : "No providers found"}
          </h3>
          <p className="text-slate-500">
            {providers.length === 0 
              ? "Be the first to create a professional profile!"
              : "Try adjusting your search criteria"
            }
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredProviders.map(provider => (
            <Card key={provider.id} className="hover:shadow-xl transition-all duration-300 bg-white/70 backdrop-blur-sm border-slate-200/50 hover:-translate-y-1">
              <CardHeader className="pb-3">
                <div className="flex items-center gap-3">
                  {provider.profile_picture ? (
                    <img
                      src={provider.profile_picture.startsWith('data:') ? provider.profile_picture : `${API_URL}${provider.profile_picture}`}
                      alt={provider.name}
                      className="w-12 h-12 rounded-full object-cover border-2"
                      style={{borderColor: 'var(--brand-navy)'}}
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.nextSibling.style.display = 'flex';
                      }}
                    />
                  ) : null}
                  <div
                    className={`w-12 h-12 rounded-full flex items-center justify-center text-white font-semibold ${provider.profile_picture ? 'hidden' : 'flex'}`}
                    style={{background: 'linear-gradient(135deg, var(--brand-navy) 0%, var(--brand-navy-light) 100%)'}}
                  >
                    {provider.name.charAt(0).toUpperCase()}
                  </div>
                  
                  <div className="flex-1">
                    <CardTitle className="text-lg flex items-center gap-2" style={{color: 'var(--brand-navy)'}}>
                      {provider.name}
                      {provider.in_network && (
                        <Zap 
                          className="w-4 h-4" 
                          style={{color: 'var(--brand-red)'}}
                          title="Already in your network"
                        />
                      )}
                    </CardTitle>
                    {provider.business_name && (
                      <p className="text-sm text-gray-600">{provider.business_name}</p>
                    )}
                  </div>
                </div>
              </CardHeader>

              <CardContent className="pt-0">
                {/* Location and Experience */}
                <div className="flex items-center gap-4 text-sm text-gray-600 mb-3">
                  {provider.location && (
                    <div className="flex items-center gap-1">
                      <MapPin className="w-4 h-4" />
                      <span>{provider.location}</span>
                    </div>
                  )}
                  {provider.years_experience && (
                    <div className="flex items-center gap-1">
                      <Award className="w-4 h-4" />
                      <span>{provider.years_experience}y exp</span>
                    </div>
                  )}
                </div>

                {/* Bio Preview */}
                {provider.bio && (
                  <p className="text-sm text-gray-700 mb-3 line-clamp-2">
                    {provider.bio}
                  </p>
                )}

                {/* Services */}
                {provider.services_provided && provider.services_provided.length > 0 && (
                  <div className="mb-4">
                    <div className="flex flex-wrap gap-2">
                      {provider.services_provided.slice(0, 3).map((service, index) => (
                        <Badge
                          key={index}
                          variant="secondary"
                          className={`${getServiceColor(service)} text-xs`}
                        >
                          {service}
                        </Badge>
                      ))}
                      {provider.services_provided.length > 3 && (
                        <Badge variant="secondary" className="text-xs bg-gray-100 text-gray-600">
                          +{provider.services_provided.length - 3} more
                        </Badge>
                      )}
                    </div>
                  </div>
                )}

                {/* Mutual Connections - Social Media Style */}
                {provider.mutual_connections_count > 0 && provider.mutual_connections && provider.mutual_connections.length > 0 && (
                  <div className="mb-3">
                    <div 
                      className="flex items-center gap-2 p-2 rounded-lg cursor-pointer hover:opacity-80 transition-opacity" 
                      style={{backgroundColor: 'var(--brand-tan-light, #f0f0f0)'}}
                      onClick={() => handleMutualConnectionsClick(provider.mutual_connections, provider.name)}
                    >
                      <Users className="w-4 h-4" style={{color: 'var(--brand-navy)'}} />
                      <span className="text-sm font-medium underline decoration-dotted" style={{color: 'var(--brand-navy)'}}>
                        {(() => {
                          const connections = provider.mutual_connections;
                          if (connections.length === 1) {
                            return `Also known by ${connections[0].name}`;
                          } else if (connections.length === 2) {
                            return `Also known by ${connections[0].name} and ${connections[1].name}`;
                          } else if (connections.length === 3) {
                            return `Also known by ${connections[0].name}, ${connections[1].name} and ${connections[2].name}`;
                          } else {
                            return `Also known by ${connections[0].name}, ${connections[1].name} and ${connections.length - 2} others in your network`;
                          }
                        })()}
                      </span>
                    </div>
                  </div>
                )}

                {/* Reviews Section */}
                {provider.total_reviews > 0 && (
                  <div className="mb-3">
                    <div 
                      className="flex items-center gap-2 p-2 rounded-lg cursor-pointer hover:opacity-80 transition-opacity" 
                      style={{backgroundColor: 'var(--brand-tan-light, #f0f0f0)'}}
                      onClick={() => handleReviewsClick(provider)}
                    >
                      <Star className="w-4 h-4 fill-current" style={{color: '#ea580c'}} />
                      <div className="flex items-center gap-2 text-sm">
                        <span className="font-medium" style={{color: 'var(--brand-navy)'}}>
                          {provider.avg_rating.toFixed(1)} stars
                        </span>
                        <span className="text-gray-600">•</span>
                        <span 
                          className="font-medium underline decoration-dotted hover:text-blue-600 transition-colors" 
                          style={{color: 'var(--brand-navy)'}}
                        >
                          {provider.total_reviews} review{provider.total_reviews !== 1 ? 's' : ''}
                        </span>
                      </div>
                    </div>

                    {/* Show latest review preview */}
                    {provider.recent_reviews && provider.recent_reviews.length > 0 && provider.recent_reviews[0].review && (
                      <div className="mt-2 pl-6">
                        <p className="text-xs text-gray-600 italic line-clamp-2">
                          "{provider.recent_reviews[0].review}"
                        </p>
                      </div>
                    )}
                  </div>
                )}

                {/* Rate and Availability */}
                <div className="flex items-center justify-between mb-4">
                  {provider.hourly_rate && (
                    <div className="flex items-center gap-1 text-sm">
                      <DollarSign className="w-4 h-4 text-green-600" />
                      <span className="font-medium text-green-600">{provider.hourly_rate}</span>
                    </div>
                  )}
                  
                  {provider.availability && (
                    <Badge
                      variant="outline"
                      className={`text-xs ${
                        provider.availability === 'Available' ? 'bg-green-50 text-green-700 border-green-200' :
                        provider.availability === 'Busy' ? 'bg-yellow-50 text-yellow-700 border-yellow-200' :
                        'bg-red-50 text-red-700 border-red-200'
                      }`}
                    >
                      {provider.availability}
                    </Badge>
                  )}
                </div>

                {/* Action Button */}
                <Button
                  onClick={() => setSelectedProvider(provider.id)}
                  className="w-full text-white"
                  style={{
                    background: 'linear-gradient(135deg, var(--brand-navy) 0%, var(--brand-navy-dark) 100%)'
                  }}
                >
                  <Eye className="w-4 h-4 mr-2" />
                  View Profile
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Public Profile Modal */}
      {selectedProvider && (
        <PublicProfile
          userId={selectedProvider}
          isModal={true}
          onClose={() => {
            setSelectedProvider(null);
            // Check if we need to show network alert
            const alertShown = sessionStorage.getItem('network_alert');
            if (alertShown) {
              setNetworkAlert(alertShown);
              sessionStorage.removeItem('network_alert');
              setTimeout(() => setNetworkAlert(null), 4000);
            }
          }}
        />
      )}

      {/* Mutual Connections Modal */}
      <MutualConnectionsModal
        isOpen={mutualConnectionsModal.isOpen}
        onClose={closeMutualConnectionsModal}
        connections={mutualConnectionsModal.connections}
        providerName={mutualConnectionsModal.providerName}
      />

      {/* Reviews Modal */}
      <ReviewsModal
        isOpen={reviewsModal.isOpen}
        onClose={() => setReviewsModal({ isOpen: false, provider: null })}
        provider={reviewsModal.provider}
      />
    </div>
  );
};

export default ServiceProviders;